import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'
import type { Session, SessionHighlights } from '@/types/database'

// Helper to extract score from highlights (only some types have overall_score)
function getHighlightScore(highlights: SessionHighlights | null): number | null {
  if (!highlights) return null
  if ('overall_score' in highlights) return highlights.overall_score
  return null
}
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ArrowLeft } from 'lucide-react'

export function History() {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const [sessions, setSessions] = useState<Session[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const fetchSessions = async () => {
      if (!profile?.id) return

      const result = await supabase
        .from('sessions')
        .select('*')
        .eq('user_id', profile.id)
        .order('created_at', { ascending: false })
        .limit(20)

      if (result.error) {
        console.error('Error fetching sessions:', result.error)
      } else {
        setSessions((result.data as Session[]) || [])
      }
      setIsLoading(false)
    }

    fetchSessions()
  }, [profile?.id])

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return '-'
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}m ${secs}s`
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-gradient-to-r from-amber-50/60 via-white to-orange-50/60 border-b border-amber-200/30 shadow-sm">
        <div className="container mx-auto px-4 py-3 md:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Back</span>
            </Button>
            <h1 className="text-lg md:text-xl font-semibold text-gray-800">
              <span className="sm:hidden">History</span>
              <span className="hidden sm:inline">Session History</span>
            </h1>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto space-y-4">
          {isLoading ? (
            <>
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-2xl border border-amber-200/50 shadow-lg overflow-hidden">
                  <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
                  <div className="p-4 md:p-6">
                    <Skeleton className="h-6 w-32 mb-2" />
                    <Skeleton className="h-4 w-48" />
                  </div>
                </div>
              ))}
            </>
          ) : sessions.length === 0 ? (
            <div className="bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-2xl border border-amber-200/50 shadow-lg overflow-hidden">
              <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
              <div className="p-8 text-center">
                <p className="text-gray-600">No sessions yet</p>
                <Button
                  className="mt-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/25"
                  onClick={() => navigate('/practice')}
                >
                  Start Your First Session
                </Button>
              </div>
            </div>
          ) : (
            sessions.map((session) => {
              const isClickable = session.status === 'completed'
              const isCancelled = session.status === 'cancelled'

              return (
                <div
                  key={session.id}
                  className={`bg-gradient-to-br from-amber-50/80 via-white to-orange-50/80 rounded-2xl border border-amber-200/50 shadow-lg overflow-hidden transition-all ${
                    isClickable
                      ? 'cursor-pointer hover:shadow-xl hover:border-amber-300'
                      : ''
                  } ${isCancelled ? 'opacity-60' : ''}`}
                  onClick={() => isClickable && navigate(`/session/${session.id}`)}
                >
                  <div className="h-1.5 bg-gradient-to-r from-amber-400 via-orange-400 to-rose-400" />
                  <div className="p-4 md:p-6">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className={`text-lg font-semibold ${isCancelled ? 'text-gray-400' : 'text-gray-800'}`}>
                        {formatDate(session.created_at)}
                      </h3>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          session.status === 'completed'
                            ? 'bg-emerald-100 text-emerald-800'
                            : session.status === 'processing'
                            ? 'bg-amber-100 text-amber-800'
                            : session.status === 'failed'
                            ? 'bg-rose-100 text-rose-800'
                            : session.status === 'cancelled'
                            ? 'bg-gray-100 text-gray-500'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {session.status === 'cancelled' ? 'Too short' : session.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">
                      Duration: {formatDuration(session.duration_seconds)}
                      {session.highlights && session.status !== 'cancelled' && getHighlightScore(session.highlights) !== null && (
                        <span className="ml-4">
                          Score: {getHighlightScore(session.highlights)}/100
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              )
            })
          )}
        </div>
      </main>
    </div>
  )
}
